import { Box, Heading, HStack, Link, SimpleGrid, Skeleton, Text, VStack } from "@chakra-ui/react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowLeft, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useUser } from "../../contexts/userContext";
import { useActivities } from "../../hooks/useActivities";
import * as routes from "../../routes";
import {
  actionButton,
  glassCard,
  mutedText,
  pageContainer,
  sectionHeading,
} from "../../themes/design";
import type { ActivityDetail } from "../../types/activities";
import {
  formatDate,
  formatDistance,
  formatDuration,
  formatPace,
  formatSpeed,
  isPaceSport,
  sportIcon,
} from "../../utils/activity-format";

interface StatBoxProps {
  label: string;
  value: string;
}

const StatBox: React.FC<StatBoxProps> = ({ label, value }) => (
  <VStack gap={0}>
    <Text {...mutedText} fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
      {label}
    </Text>
    <Text color="primary.100" fontWeight="600" fontSize="lg">
      {value}
    </Text>
  </VStack>
);

const ActivityDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { activityId } = useParams<{ activityId: string }>();
  const { getActivity } = useActivities();
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!user || !activityId) return;
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    getActivity(activityId)
      .then((result) => {
        if (!cancelled) setActivity(result);
      })
      .catch((err) => {
        console.error("Failed to fetch activity:", err);
        if (!cancelled) {
          setActivity(null);
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, activityId, getActivity]);

  if (!user) return null;

  if (isLoading) {
    return (
      <Box {...pageContainer} maxW="800px">
        <Skeleton h="24px" w="200px" mb={4} />
        <Skeleton h="40px" w="100%" mb={2} />
        <Skeleton h="200px" w="100%" borderRadius="xl" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box {...pageContainer} maxW="800px">
        <Link as={RouterLink} to={routes.ACTIVITIES_PAGE} color="cyan.300" fontSize="sm">
          <FontAwesomeIcon icon={faArrowLeft as IconProp} /> {t("BackToActivities")}
        </Link>
        <Text color="red.400" py={8}>
          {t("FetchError")}
        </Text>
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box {...pageContainer} maxW="800px">
        <Link as={RouterLink} to={routes.ACTIVITIES_PAGE} color="cyan.300" fontSize="sm">
          <FontAwesomeIcon icon={faArrowLeft as IconProp} /> {t("BackToActivities")}
        </Link>
        <Text {...mutedText} py={8}>
          {t("ActivityNotFound")}
        </Text>
      </Box>
    );
  }

  const isPace = isPaceSport(activity.sport_type);

  return (
    <Box {...pageContainer} maxW="800px">
      {/* Back link */}
      <Link
        as={RouterLink}
        to={routes.ACTIVITIES_PAGE}
        color="cyan.300"
        fontSize="sm"
        _hover={{ textDecoration: "none", color: "cyan.200" }}
      >
        <FontAwesomeIcon icon={faArrowLeft as IconProp} /> {t("BackToActivities")}
      </Link>

      {/* Activity header */}
      <HStack gap={3}>
        <Text fontSize="2xl">{sportIcon(activity.sport_type)}</Text>
        <VStack alignItems="flex-start" gap={0}>
          <Heading {...sectionHeading} mb={0}>
            {activity.name}
          </Heading>
          {activity.activity_start_date && (
            <Text {...mutedText} fontSize="sm">
              {formatDate(activity.activity_start_date)}
            </Text>
          )}
        </VStack>
      </HStack>

      {/* Stats card */}
      <Box {...glassCard}>
        <Heading color="cyan.300" size="sm" letterSpacing="0.05em" mb={4} textAlign="center">
          {t("KeyStats")}
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 3 }} gap={6}>
          <StatBox label={t("Distance")} value={formatDistance(activity.distance)} />
          <StatBox label={t("Time")} value={formatDuration(activity.moving_time)} />
          <StatBox
            label={isPace ? t("Pace") : t("Speed")}
            value={
              isPace
                ? formatPace(activity.distance, activity.moving_time)
                : formatSpeed(activity.distance, activity.moving_time)
            }
          />
          {activity.total_elevation_gain != null && activity.total_elevation_gain > 0 && (
            <StatBox
              label={t("Elevation")}
              value={`${Math.round(activity.total_elevation_gain)} m`}
            />
          )}
          {activity.average_heartrate != null && (
            <StatBox
              label={t("AvgHeartRate")}
              value={`${Math.round(activity.average_heartrate)} bpm`}
            />
          )}
          {activity.max_heartrate != null && (
            <StatBox
              label={t("MaxHeartRate")}
              value={`${Math.round(activity.max_heartrate)} bpm`}
            />
          )}
        </SimpleGrid>
      </Box>

      {/* Strava link — only shown for Strava activities */}
      {activity.service_name === "Strava" && (
        <Link
          href={`https://www.strava.com/activities/${activity.activity_id}`}
          isExternal
          _hover={{ textDecoration: "none" }}
        >
          <Box {...glassCard} textAlign="center" {...actionButton} py={3}>
            {t("ViewOnStrava")}{" "}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare as IconProp} size="sm" />
          </Box>
        </Link>
      )}
    </Box>
  );
};

export default ActivityDetailPage;
